/**
 * Tenant Context Hook
 *
 * This hook ensures that components wait for the tenant context to be fully loaded
 * before making Apollo queries. This prevents race conditions where queries are made
 * before the correct tenant ID is available in the headers.
 */

import { useAuthStore } from "../stores/auth-store";

export interface TenantContextState {
  isReady: boolean;
  isLoading: boolean;
  tenantId: string | undefined;
  userRole: string | undefined;
}

/**
 * Hook that returns the tenant context state and waits for it to be ready
 */
export function useTenantContext(): TenantContextState {
  const { isAuthenticated, isLoading, isTenantContextReady, user } =
    useAuthStore((state) => ({
      isAuthenticated: state.isAuthenticated,
      isLoading: state.isLoading,
      isTenantContextReady: state.isTenantContextReady,
      user: state.user,
    }));

  // Tenant context is ready when:
  // 1. Not loading AND
  // 2. Either not authenticated (public access) OR authenticated with tenant context ready
  const isReady = !isLoading && (!isAuthenticated || isTenantContextReady);

  return {
    isReady,
    isLoading,
    tenantId: user?.tenantId,
    userRole: user?.role,
  };
}

/**
 * Hook that can be used to conditionally render components based on tenant context readiness
 */
export function useTenantContextGuard() {
  const { isReady, isLoading } = useTenantContext();

  return {
    isReady,
    isLoading,
  };
}
